import type { AutocompleteProps, FilterOptionsState } from '@mui/material';
import type { ListChildComponentProps } from 'react-window';
import type { DirectoryProvider } from '#services/api/provider.ts';
import type { BaseAutocompleteInputProps } from '../Autocomplete.types.ts';

import * as React from 'react';
import { styled } from '@mend/mui';
import { Autocomplete, Popper, TextField } from '@mui/material';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { useQuery } from '@tanstack/react-query';
import { FixedSizeList } from 'react-window';

import { matchSorter } from '#lib/match-sorter.ts';
import { providerDirectory } from '#lib/react-query/queries.ts';
import { getTextFieldProps } from '../Autocomplete.utils.ts';
import {
  ProviderOption,
  type ProviderOptionProps,
  providerOptionHeight,
} from './ProviderOption.tsx';
import { keepDirectoryOrder, searchKeys, selectedFirst } from './ProvidersAutocomplete.utils.ts';

/**
 * Virtualization example taken from MUI's docs with some modifications.
 * @see https://mui.com/material-ui/react-autocomplete/#virtualization
 */
const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

const listboxPadding = 8;

/** How many options the popup shows before it scrolls. */
const maxVisibleOptions = 8;

/**
 * Shown when the directory read failed. Named rather than counted or hidden: the numbers below are
 * still narrowed by whatever the URL says, so an empty Provider Filter with no explanation reads as
 * a scope that vanished.
 */
const directoryUnreadable = "Couldn't load providers. Reload to try again.";

type RenderedRow = React.ReactElement<ProviderOptionProps>;

function renderRow({ data, index, style }: ListChildComponentProps<RenderedRow[]>) {
  const row = data[index];
  if (!row) return null;

  return React.cloneElement(row, {
    style: { ...style, top: Number(style.top) + listboxPadding },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>(function OuterElementType(props, ref) {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent({ children, ...other }, ref) {
    // Every child is one `renderOption` result. Safe to treat as a flat list because the component
    // doesn't accept `groupBy` — grouping is what would nest option elements under headers.
    const rows = children as RenderedRow[];
    const visibleHeight = Math.min(rows.length, maxVisibleOptions) * providerOptionHeight;

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            height={visibleHeight + 2 * listboxPadding}
            width="100%"
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemData={rows}
            itemCount={rows.length}
            itemSize={providerOptionHeight}
            overscanCount={5}
          >
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

/** No options to show yet, at a stable reference — a fresh `[]` each render re-runs MUI's filter. */
const noProviders: DirectoryProvider[] = [];

interface ProvidersAutocompleteProps<Multiple extends boolean | undefined = undefined>
  extends
    Omit<
      AutocompleteProps<DirectoryProvider, Multiple, false, false>,
      | 'loading'
      | 'options'
      | 'getOptionKey'
      | 'getOptionLabel'
      | 'filterOptions'
      | 'filterSelectedOptions'
      | 'isOptionEqualToValue'
      | 'onOpen'
      | 'renderInput'
      | 'renderOption'
      | 'renderValue'
      | 'freeSolo'
      | 'groupBy'
      | 'disableListWrap'
      | 'slots'
      | 'slotProps'
    >,
    Omit<BaseAutocompleteInputProps, 'dataFilter'> {
  /** Include the Unassigned sentinel alongside real providers; enabled by default for filters. */
  includeUnassigned?: boolean;
}

/**
 * Select from the organization's providers and provider admins, optionally including Unassigned, listed on
 * open and filtered client-side from the session's cached directory — no per-keystroke request, and
 * nothing to type before the list appears.
 *
 * Ported from the portal's picker via `apps/app`, which is where virtualization was solved. The
 * ten-thousand-provider precedent people cite is a paginated table; that many option elements in a
 * popup does not render acceptably, so the virtualization plumbing here is required rather than an
 * optimization. Search is the repo's own `matchSorter` rather than the port's hand-rolled regex, so
 * this picker filters like every other one in Signal. Unifying the three copies is a follow-up.
 */
export function ProvidersAutocomplete<Multiple extends boolean | undefined = undefined>({
  inputName = 'provider',
  inputLabel = 'Provider',
  inputHelperText,
  inputError,
  inputRequired,
  inputSize = 'small',
  fullWidth = true,
  clearOnBlur = false,
  selectOnFocus = true,
  handleHomeEndKeys = true,
  disabled,
  value,
  multiple,
  includeUnassigned = true,
  ...props
}: ProvidersAutocompleteProps<Multiple>): JSX.Element {
  const query = useQuery(providerDirectory);

  /**
   * Until the directory answers there are no names to render, and — because the control reports its
   * value to the filter when the popup closes — an interaction while it's empty would report "no
   * providers" and drop the filter the chips were about to show. Held shut in both states, and said
   * out loud in the failing one, where waiting won't fix it.
   */
  const directoryUnavailable = !query.data;

  /**
   * Whom to float to the top, sampled when the popup opens rather than read live: selecting a
   * provider with the list open would otherwise slide it to the top under the pointer, and the next
   * click would land on whatever row took its place.
   */
  const [pinnedIds, setPinnedIds] = React.useState<ReadonlySet<DirectoryProvider['id']>>(
    () => new Set()
  );

  const filterProviders = React.useCallback(
    (options: DirectoryProvider[], state: FilterOptionsState<DirectoryProvider>) =>
      selectedFirst(
        matchSorter(
          includeUnassigned ? options : options.filter((provider) => !provider.isUnassigned),
          state.inputValue.trim(),
          {
            keys: searchKeys,
            baseSort: keepDirectoryOrder,
          }
        ),
        pinnedIds
      ),
    [includeUnassigned, pinnedIds]
  );

  return (
    <Autocomplete
      multiple={multiple}
      value={value}
      freeSolo={false}
      fullWidth={fullWidth}
      clearOnBlur={clearOnBlur}
      selectOnFocus={selectOnFocus}
      handleHomeEndKeys={handleHomeEndKeys}
      disabled={disabled || directoryUnavailable}
      loading={query.isLoading}
      options={query.data ?? noProviders}
      getOptionKey={(option) => option.id}
      getOptionLabel={(option) => option.name}
      // Selected providers stay listed so a chip that overflowed the field's one-tag limit can
      // still be deselected from the dropdown.
      filterSelectedOptions={false}
      filterOptions={filterProviders}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onOpen={() => {
        const selected: DirectoryProvider[] = Array.isArray(value) ? value : value ? [value] : [];
        setPinnedIds(new Set(selected.map((provider) => provider.id)));
      }}
      renderInput={(params) => (
        <TextField
          {...getTextFieldProps({ params, multiple, inputRequired })}
          name={inputName}
          label={inputLabel}
          helperText={query.isError ? directoryUnreadable : inputHelperText}
          error={inputError ?? query.isError}
          size={inputSize}
        />
      )}
      renderOption={({ key, ...optionProps }, option, state) => (
        <ProviderOption key={key} props={optionProps} option={option} state={state} />
      )}
      /**
       * Disabled because with virtualization we only render a subset of elements and the default
       * wrapping would loop between them only, making it seem as if it was broken.
       */
      disableListWrap
      /**
       * Custom components wrapping the virtualization functionality.
       */
      slots={{ popper: StyledPopper }}
      slotProps={{ listbox: { component: ListboxComponent } }}
      {...props}
    />
  );
}
